/**
 * Converts a YYYY-MM-DD backend date string to DD/MM/YYYY without any Date
 * object conversion, avoiding the GMT-3 D-1 shift entirely.
 * Also handles ISO datetime strings (slices to the date part before formatting).
 */
export const formatDateBR = (dateStr?: string): string => {
  if (!dateStr) return "—";
  const [y, m, d] = dateStr.slice(0, 10).split("-");
  if (!d) return dateStr;
  return `${d}/${m}/${y}`;
};

// ===== CNPJ alfanumérico (algoritmo oficial Serpro) =====
// Corpo: 12 caracteres [A-Z0-9]; DV: 2 dígitos numéricos.
// Retrocompatível: um CNPJ 100% numérico gera exatamente o mesmo DV de antes.
const CNPJ_TAMANHO_SEM_DV = 12;
const CNPJ_REGEX_SEM_DV = /^[A-Z\d]{12}$/;
const CNPJ_REGEX = /^[A-Z\d]{12}\d{2}$/;
const CNPJ_REGEX_CARACTERES_NAO_PERMITIDOS = /[^A-Z\d./-]/i;
const CNPJ_MASCARA = /[./-]/g;
const CNPJ_VALOR_BASE = "0".charCodeAt(0);
const CNPJ_PESOS_DV = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_ZERADO = "00000000000000";

// Normaliza um CNPJ/CPF removendo só máscara e espaços e forçando maiúsculo,
// preservando as letras do CNPJ alfanumérico (dígitos ficam inalterados).
export const cleanCnpj = (value?: string): string =>
  (value ?? "").replace(/[./\s-]/g, "").toUpperCase().trim();

export const calculaDvCnpj = (cnpj: string): string => {
  if (!CNPJ_REGEX_CARACTERES_NAO_PERMITIDOS.test(cnpj)) {
    const corpo = cnpj.replace(CNPJ_MASCARA, "");
    if (
      CNPJ_REGEX_SEM_DV.test(corpo) &&
      corpo !== CNPJ_ZERADO.substring(0, CNPJ_TAMANHO_SEM_DV)
    ) {
      let somatorioDV1 = 0;
      let somatorioDV2 = 0;
      for (let i = 0; i < CNPJ_TAMANHO_SEM_DV; i++) {
        const asciiDigito = corpo.charCodeAt(i) - CNPJ_VALOR_BASE;
        somatorioDV1 += asciiDigito * CNPJ_PESOS_DV[i + 1];
        somatorioDV2 += asciiDigito * CNPJ_PESOS_DV[i];
      }
      const dv1 = somatorioDV1 % 11 < 2 ? 0 : 11 - (somatorioDV1 % 11);
      somatorioDV2 += dv1 * CNPJ_PESOS_DV[CNPJ_TAMANHO_SEM_DV];
      const dv2 = somatorioDV2 % 11 < 2 ? 0 : 11 - (somatorioDV2 % 11);
      return `${dv1}${dv2}`;
    }
  }
  throw new Error(
    "Não é possível calcular o DV pois o CNPJ fornecido é inválido"
  );
};

export const isValidCnpj = (cnpj: string): boolean => {
  if (!cnpj || CNPJ_REGEX_CARACTERES_NAO_PERMITIDOS.test(cnpj)) return false;
  const semMascara = cnpj.replace(CNPJ_MASCARA, "");
  if (!CNPJ_REGEX.test(semMascara) || semMascara === CNPJ_ZERADO) return false;
  const dvInformado = semMascara.substring(CNPJ_TAMANHO_SEM_DV);
  const dvCalculado = calculaDvCnpj(semMascara.substring(0, CNPJ_TAMANHO_SEM_DV));
  return dvInformado === dvCalculado;
};

export const formatCnpj = (value?: string): string => {
  if (!value) return "";
  const clean = cleanCnpj(value).slice(0, 14);
  return clean
    .replace(/^([A-Z\d]{2})([A-Z\d])/, "$1.$2")
    .replace(/^([A-Z\d]{2})\.([A-Z\d]{3})([A-Z\d])/, "$1.$2.$3")
    .replace(/\.([A-Z\d]{3})([A-Z\d])/, ".$1/$2")
    .replace(/([A-Z\d]{4})(\d)/, "$1-$2");
};

export const formatCep = (value: string): string => {
  const numbersOnly = value.replace(/\D/g, "");
  if (numbersOnly.length <= 8) {
    return numbersOnly.replace(/^(\d{5})(\d)/, "$1-$2");
  }
  return value;
};

export const applyCnpjMask = (cnpj: string): string => {
  return cnpj.replace(
    /([A-Z\d]{2})([A-Z\d]{3})([A-Z\d]{3})([A-Z\d]{4})(\d{2})/,
    "$1.$2.$3/$4-$5"
  );
};

export const applyCepMask = (cep: string): string => {
  return cep.replace(/(\d{5})(\d{3})/, "$1-$2");
};

export const generateUsernamePlaceholder = (
  apelido: string | undefined,
  type: string
): string => {
  const apelidoLower = apelido?.toLowerCase() || "[apelido]";
  return `qualquercoisa+${apelidoLower}+${type}@planaltocapital.com.br`;
};

export const generateUsernameHelperText = (
  apelido: string | undefined,
  type: string
): string => {
  const apelidoLower = apelido?.toLowerCase() || "[apelido]";
  return `Formato: qualquercoisa+${apelidoLower}+${type}@planaltocapital.com.br`;
};
